import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CsrfTokenService } from './csrf-token.service';
import { FORCED_NAV } from './forced-nav';
import { LogoutService } from './logout.service';

export const authInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (isStaticAsset(req)) {
    return next(req);
  }

  const logout = inject(LogoutService);
  const router = inject(Router);
  const toastr = inject(ToastrService);

  const request = withJsonHeaders(req);
  return sendRequest(request, next).pipe(
    catchError((error: unknown) => {
      handleHttpError(error, request.url, logout, router, toastr);
      return throwError(() => error);
    }),
  );
};

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const CSRF_EXEMPT_PATHS = [
  '/Auth/login',
  '/Auth/forgot-password',
  '/Auth/reset-password',
];
const CSRF_HEADER_NAME = 'X-CSRF-TOKEN';

function sendRequest(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  if (!requiresCsrfToken(req)) {
    return next(req);
  }

  const csrfTokenService = inject(CsrfTokenService);
  return csrfTokenService.getToken().pipe(
    switchMap((token) => next(req.clone({
      setHeaders: { [CSRF_HEADER_NAME]: token },
    }))),
  );
}

function requiresCsrfToken(req: HttpRequest<unknown>): boolean {
  return req.url.startsWith(environment.baseURl) &&
    !SAFE_METHODS.has(req.method.toUpperCase()) &&
    !CSRF_EXEMPT_PATHS.some((path) =>
      req.url.startsWith(`${environment.baseURl}${path}`));
}

function isStaticAsset(req: HttpRequest<unknown>): boolean {
  return req.url.endsWith('.html') || req.url.endsWith('.htm');
}

// window.location (not Router.url) because the app initializer's session
// check can fail before the router has resolved the initial navigation.
function isOnAnonymousAuthPage(): boolean {
  return window.location.pathname.includes('/authentication');
}

function withJsonHeaders(req: HttpRequest<unknown>): HttpRequest<unknown> {
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (!(req.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  return req.clone({ setHeaders: headers, withCredentials: true });
}

function handleHttpError(
  error: unknown,
  requestUrl: string,
  logout: LogoutService,
  router: Router,
  toastr: ToastrService,
): void {
  if (!(error instanceof HttpErrorResponse)) {
    return;
  }

  const apiMessage = getApiErrorMessage(error);

  switch (error.status) {
    case 401:
      if (apiMessage) {
        toastr.error(apiMessage);
      }

      // On anonymous pages (login, forgot/reset password) a 401 just means
      // "no session yet" - forcing a logout navigation would rip the user
      // away from the page (e.g. off a password-reset link) for no reason.
      if (!isOnAnonymousAuthPage()) {
        logout.logout();
      }
      return;
    case 403:
      toastr.error(apiMessage || 'You are not authorized to perform this action.');
      return;
    case 429:
      toastr.warning('Too many requests. Please wait a moment and try again.');
      return;
    case 404:
      if (requestUrl.startsWith(environment.baseURl)) {
        toastr.error(apiMessage || 'The requested data was not found.');
        return;
      }

      router.navigate(['authentication/error404'], FORCED_NAV);
      return;
    case 0:
      router.navigate(['authentication/maintenance'], FORCED_NAV);
      return;
    case 500:
      if (apiMessage) {
        toastr.error(apiMessage);
      }

      router.navigate(['authentication/maintenance'], FORCED_NAV);
      return;
  }
}

function getApiErrorMessage(error: HttpErrorResponse): string {
  const body = error.error;

  if (!body) {
    return '';
  }

  if (typeof body === 'string') {
    return body;
  }

  const errors = body.errors;
  if (Array.isArray(errors) && errors.length) {
    return errors.map(toErrorMessage).filter(Boolean).join('\n');
  }

  if (errors && typeof errors === 'object') {
    const messages = Object.values(errors)
      .flat()
      .map(toErrorMessage)
      .filter(Boolean);
    if (messages.length) {
      return messages.join('\n');
    }
  }

  return typeof body.message === 'string' ? body.message : '';
}

function toErrorMessage(error: unknown): string {
  if (!error) {
    return '';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (typeof error === 'number' || typeof error === 'boolean') {
    return String(error);
  }

  if (typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') {
      return message;
    }
  }

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}
